import * as ActionTypes from '../actions/actionTypes';
import {DATA_SETS, FFM_OPTION} from '../Constants';
import {
  calculateTableData,
  convertToGraphData,
  tableMetaData
} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {runParityCalcsFromConfig} from '../utils/behaviour_models/shared/parity';
import {
  calculateCROS,
  calculateDIR,
  calculateEROS,
  calculateFH,
  calculateFI,
  calculatePGO10,
  calculatePGOCR,
  calculateSFFM,
  calculateSROS
} from '../utils/behaviour_models/mallee_heath/calculations';
import {DEFAULT_FFL, DEFAULT_OSC, DEFAULT_OSH} from '../utils/behaviour_models/mallee_heath/constants';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';

const extractVariables = (state) => {
  return {
    overstorey_cover: state.overstorey_cover,
    overstorey_height: state.overstorey_height,
    ffm_option: state.ffm_option,
    fine_fuel_load: state.fine_fuel_load,
    lag_temp_rh: state.lag_temp_rh
  };
};

const VALUES_CONFIG = [
  { key: 'DIR', displayName: 'Direction of Spread', calc: calculateDIR, decPlaces: 0, arrow: 'fa-arrow-up' },
  { key: 'SFFM', displayName: 'Surface FFMC (%)', calc: calculateSFFM, decPlaces: 1 },
  {
    key: 'PGO10', displayName: 'Prob Go / NoGo (10m wind)', calc: calculatePGO10, decPlaces: 2,
    colors: {
      scale: {
        '0.4': 'rgba(217, 225, 242, 0.7)',
        '0.5': 'rgba(223, 214, 228, 0.7)',
        '0.6': 'rgba(229, 202, 214, 0.7)',
        '0.7': 'rgba(235, 191, 199, 0.7)',
        '0.8': 'rgba(242, 179, 184, 0.7)',
        '0.9': 'rgba(248, 167, 170, 0.7)'
      },
      else: 'rgba(255, 155, 155, 0.7)'
    }
  },
  {
    key: 'PGOCR', displayName: 'Prob Go / NoGo (Crown)', calc: calculatePGOCR, decPlaces: 2,
    colors: {
      scale: {
        '0.5': 'rgba(221, 235, 247, 0.7)',
        '0.6': 'rgba(227, 220, 229, 0.7)',
        '0.7': 'rgba(234, 204, 211, 0.7)',
        '0.8': 'rgba(241, 187, 192, 0.7)',
        '0.9': 'rgba(248, 171, 174, 0.7)'
      },
      else: 'rgba(255, 155, 155, 0.7)'
    }
  },
  { key: 'SROS', displayName: 'Surface ROS (m/h)', calc: calculateSROS, decPlaces: 0 },
  { key: 'CROS', displayName: 'Crownfire ROS (m/h)', calc: calculateCROS, decPlaces: 0 },
  { key: 'EROS', displayName: 'Effective ROS (m/h)', calc: calculateEROS, decPlaces: 0 },
  { key: 'FI', displayName: 'Fireline Intensity (kW/m) ', calc: calculateFI, decPlaces: 0 },
  {
    key: 'FH', displayName: 'Flame Height (m)', calc: calculateFH, decPlaces: 2,
    colors: {
      scale: {
        '0': 'rgba(99, 190, 123, 0.7)',
        '1': 'rgba(138, 201, 125, 0.7)',
        '2': 'rgba(177, 212, 127, 0.7)',
        '3': 'rgba(216, 223, 129, 0.7)',
        '4': 'rgba(255, 235, 132, 0.7)',
        '5': 'rgba(254, 203, 126, 0.7)',
        '6': 'rgba(252, 170, 120, 0.7)',
        '7': 'rgba(250, 138, 114, 0.7)'
      },
      else: 'rgba(248, 105, 107, 0.7)'
    }
  }
];

const GRAPHS_CONFIG = [
  {
    title: 'Surface Rate of Spread',
    id: 'SROS',
    type: 'LineChart',
    YAxisLabel: 'm/h',
    series: [
      {
        name: 'ROS',
        dataKey: 'SROS',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      }
    ]
  }
];

const initialState = {
  overstorey_cover: DEFAULT_OSC,
  overstorey_height: DEFAULT_OSH,
  ffm_option: FFM_OPTION.MALLEE,
  fine_fuel_load: DEFAULT_FFL,
  lag_temp_rh: 0,
  table_data: {},
  graph_data: emptyGraphMap(GRAPHS_CONFIG),
  table_meta_data: tableMetaData(VALUES_CONFIG, DATA_SETS.MALLEE_HEATH),
  graph_meta_data: GRAPHS_CONFIG
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_BEHAVIOUR_VARIABLES:
      if (action.data_set === DATA_SETS.MALLEE_HEATH) {
        return {
          ...state,
          ...action.variables
        };
      }
      break;
    case ActionTypes.CALCULATE_MALLEE_HEATH:
      const variables = extractVariables(state);
      const modifications = action.modifications ? action.modifications.properties.data : {};
      const new_table_data =
        calculateTableData(VALUES_CONFIG,
          action.include_history,
          action.short_future,
          action.weather_data.table_data,
          variables,
          modifications);
      const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
      const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

      return {
        ...state,
        ticks,
        graph_data: new_graph_data,
        table_data: new_table_data
      };

    default:
      return state;
  }
  return state;
};

export const runParityCalcs = (wd, variables) => runParityCalcsFromConfig(VALUES_CONFIG, wd, variables);

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_mallee_heath.js